const KEY = 'dhtn_pending_destination'
const TTL_MS = 24 * 60 * 60 * 1000

interface PendingDestination {
    path: string
    createdAt: number
}

const AUTH_PATHS = new Set([
    '/login',
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
    '/oauth-callback',
])

export const normalizeInternalDestination = (value: unknown): string | null => {
    if (
        typeof value !== 'string' ||
        !value.startsWith('/') ||
        value.startsWith('//')
    ) {
        return null
    }

    try {
        const url = new URL(value, window.location.origin)
        if (
            url.origin !== window.location.origin ||
            AUTH_PATHS.has(url.pathname)
        )
            return null
        return `${url.pathname}${url.search}${url.hash}`
    } catch {
        return null
    }
}

export const setPendingDestination = (value: unknown): void => {
    const path = normalizeInternalDestination(value)
    if (!path) return

    const pending: PendingDestination = { path, createdAt: Date.now() }
    try {
        localStorage.setItem(KEY, JSON.stringify(pending))
    } catch {
        // The flow still works through router state when storage is unavailable.
    }
}

export const getPendingDestination = (): string | null => {
    try {
        const raw = localStorage.getItem(KEY)
        if (!raw) return null

        const parsed = JSON.parse(raw) as Partial<PendingDestination>
        const path = normalizeInternalDestination(parsed.path)
        if (
            path &&
            typeof parsed.createdAt === 'number' &&
            Date.now() - parsed.createdAt <= TTL_MS
        ) {
            return path
        }
    } catch {
        // Invalid entries are removed below.
    }

    try {
        localStorage.removeItem(KEY)
    } catch {
        // Storage is unavailable; there is nothing else to clean up.
    }
    return null
}

export const consumePendingDestination = (): string | null => {
    const path = getPendingDestination()
    if (path) {
        try {
            localStorage.removeItem(KEY)
        } catch {
            // The valid destination can still be used for this navigation.
        }
    }
    return path
}
